import { Accessor, createEffect, createSignal, on, onCleanup } from 'solid-js';
import { ProjectDataRepository } from './lib/project-data-repository';
import {
  ProjectDataNode,
  ProjectDataRepositoryStatus,
} from './lib/project-data-repository.types';

export function createProjectDataStatus(
  repository: ProjectDataRepository
): Accessor<ProjectDataRepositoryStatus> {
  const [status, setStatus] = createSignal(repository.status);
  const unsubscribe = repository.subscribeStatus(() =>
    setStatus(repository.status)
  );
  onCleanup(unsubscribe);
  return status;
}

export function createProjectDataNode(
  repository: ProjectDataRepository,
  nodeId: Accessor<string>
): Accessor<ProjectDataNode | undefined> {
  const [node, setNode] = createSignal<ProjectDataNode | undefined>(
    repository.getNode(nodeId())
  );
  let unsubscribe = repository.subscribeNode(nodeId(), () =>
    setNode(() => repository.getNode(nodeId()))
  );

  createEffect(
    on(
      nodeId,
      (nextNodeId) => {
        unsubscribe();
        setNode(() => repository.getNode(nextNodeId));
        unsubscribe = repository.subscribeNode(nextNodeId, () =>
          setNode(() => repository.getNode(nextNodeId))
        );
      },
      { defer: true }
    )
  );

  onCleanup(() => {
    unsubscribe();
  });
  return node;
}

export function createProjectDataChildren(
  repository: ProjectDataRepository,
  parentId: Accessor<string>
): Accessor<readonly string[]> {
  const [children, setChildren] = createSignal<readonly string[]>(
    repository.getChildren(parentId())
  );
  let unsubscribe = repository.subscribeChildren(parentId(), () =>
    setChildren(repository.getChildren(parentId()))
  );

  createEffect(
    on(
      parentId,
      (nextParentId) => {
        unsubscribe();
        setChildren(repository.getChildren(nextParentId));
        unsubscribe = repository.subscribeChildren(nextParentId, () =>
          setChildren(repository.getChildren(nextParentId))
        );
      },
      { defer: true }
    )
  );

  onCleanup(() => {
    unsubscribe();
  });
  return children;
}
